import { Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { UserSettingsService } from './user-settings.service';
import { AttributePresetStore } from './attribute-preset-store.base';
import {
  coerceFontScale,
  fontScaleFromMirror,
  resolveFontScale,
  FontScalePreset,
  ResolvedFontScale,
  FONT_SCALE_KEY,
  FONT_SCALE_STORAGE_KEY,
} from './font-scale.util';

/**
 * App-wide cache of the user's text-size preference, and the only writer of the `data-font-scale`
 * attribute on <html>. The FOURTH AttributePresetStore (after theme, contrast and colour vision),
 * the ninth reactive user preference, and SP5's mechanism.
 *
 * Everything that makes a preset work — the boot-stamp deference, the promote-an-equal-default
 * adopt, the persist-before-emit ordering, the cross-tab broadcast — is inherited and is NOT
 * re-derived here; re-deriving it by hand is how each of its siblings originally shipped broken.
 *
 * WHAT IS SPECIFIC TO THIS ONE:
 *
 *  - `mediaQuery: null`, like CvdService — but for the opposite reason, and the difference matters.
 *    CVD has no signal because no platform exposes one. Font size HAS a system signal, the
 *    browser's own default size, and it needs no query because the stylesheet honours it
 *    automatically: the scale is a PERCENTAGE, so 'system' is literally "whatever the browser is
 *    set to" and the pinned steps multiply it. A media query would be a second, worse way to ask a
 *    question CSS already answers.
 *
 *  - The resolved value is nullable, for the reason contrast's is: the default size is the
 *    attribute's ABSENCE. Unlike contrast the mirror has only TWO states in practice — the base
 *    writes PINNED_OFF_MIRROR solely for a non-default preset that resolves off, and here the only
 *    null is 'system' itself, so its mirror is removed. The sentinel is still read as a real null.
 *
 *  - NOT identity-scoped. Unlike `display.cvd`, a text size discloses nothing about its owner —
 *    it is a property of the person at the machine in the same way a theme is, so it keeps its
 *    mirror across a logout and a shared counter machine reads it for whoever sits down next.
 *    That is the same asymmetry ThemeService and ContrastService already carry, and it is a
 *    decision rather than an omission.
 */
@Injectable({ providedIn: 'root' })
export class FontScaleService extends AttributePresetStore<FontScalePreset, ResolvedFontScale> {
  constructor(
    userSettings: UserSettingsService,
    store: Store,
    zone: NgZone,
    @Inject(DOCUMENT) doc: Document,
  ) {
    super(userSettings, store, zone, doc, {
      settingsKey: FONT_SCALE_KEY,
      attributeName: 'data-font-scale',
      mirrorKey: FONT_SCALE_STORAGE_KEY,
      mirrorArea: 'local',
      mediaQuery: null,
    }, 'system');
  }

  protected coerce(v: unknown): FontScalePreset {
    return coerceFontScale(v);
  }

  protected resolve(preset: FontScalePreset): ResolvedFontScale {
    return resolveFontScale(preset);
  }

  /** Delegates to the util so the whitelist has ONE TypeScript home -- the one
   *  `src/index.boot-theme.spec.ts` runs the inline boot script against. */
  protected resolvedFromMirror(mirror: string): ResolvedFontScale | undefined {
    return fontScaleFromMirror(mirror);
  }
}
